import { RaidChatHeader, RaidChatPinAction, RaidChatPinProvider } from './RaidChatHeader.jsx';
import { RaidAttackConfirmationNotice } from './RaidAttackConfirmationNotice.jsx';
import { RaidChatHistoryControls } from './RaidChatHistoryControls.jsx';
import { RaidControlMemberAttackBadge } from './RaidControlMemberAttackBadge.jsx';
import { useRaidChatHistoryScroll } from './useRaidChatHistoryScroll.js';
import { MAX_PRACTICE_SLOTS } from '../../raid-practice/domain/raidPracticeSlots.js';
import { LoadingState } from '../../../shared/ui/LoadingState.jsx';
import { DesktopRecentResults } from '../desktop/DesktopRecentResults.jsx';
import { selectDesktopChatMessages } from '../desktop/desktopChatMessages.js';
import { DesktopRaidControlLayout } from '../desktop/DesktopRaidControlLayout.jsx';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { splitChatMessageLinks } from '../domain/chatMessageLinks.js';
import { RaidPracticeDeckDisplay } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import { RaidPracticeEditor } from '../../raid-practice/shared/RaidPracticeEditor.jsx';
import { RAID_PRACTICE_ACTION_LABELS } from '../../raid-practice/shared/raidPracticeActionLabels.js';
import { buildRaidControlNikkeAutocompleteSuggestions, resolveRaidControlNikkeAutocompleteQuery } from '../domain/raidControlChatAutocomplete.js';
import { raidControlTetrisModeLabel } from '../domain/raidControlTetrisShareCodec.js';
import { RaidControlStrategyPanel } from './RaidControlStrategyPanel.jsx';
import { PracticeProjection } from './RaidControlActionDialogs.jsx';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';
import { ChatSticker, ChatStickerPicker } from '../../stickers/ChatStickers.jsx';

const ORDER_STATUS = {
  sent: '지시 전송', acknowledged: '확인', attacking: '공격 중', held: '보류', cancelled: '취소',
};
const QUICK_MESSAGES = ['공격 가능', '잠시 대기', '막타 대기'];
const ATTACK_STALE_MS = 15 * 60_000;

// 포맷터를 한 번만 만든다. 만드는 쪽이 찍는 쪽보다 훨씬 비싸서, 메시지마다 새로 만들면
// 글 하나가 도착할 때마다 화면에 올라온 메시지 수만큼 포맷터가 생겼다.
let timeFormatter = null;

function timeLabel(value) {
  if (!value) return '';
  try {
    if (!timeFormatter) timeFormatter = new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' });
    return timeFormatter.format(new Date(value));
  } catch { return ''; }
}

function attackElapsed(value) {
  if (!value) return '';
  const elapsed = Math.max(Date.now() - new Date(value).getTime(), 0);
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return '방금 시작';
  if (minutes < 60) return `${minutes}분 경과`;
  const hours = Math.floor(minutes / 60);
  return `${hours}시간 ${minutes % 60}분 경과`;
}

function isStaleAttack(order) {
  return order?.status === 'attacking' && order.startedAt && Date.now() - new Date(order.startedAt).getTime() >= ATTACK_STALE_MS;
}

function formatDamage(value) {
  if (value === null || value === undefined) return '-';
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function nextAvailablePracticeSlot(records = []) {
  const used = new Set(records.map((record) => Number(record?.slotNo || 0)).filter((slotNo) => slotNo >= 1 && slotNo <= MAX_PRACTICE_SLOTS));
  for (let slotNo = 1; slotNo <= MAX_PRACTICE_SLOTS; slotNo += 1) if (!used.has(slotNo)) return slotNo;
  return null;
}

function messageSummary(message, order = null) {
  if (!message) return '원문을 찾을 수 없습니다.';
  if (message.type === 'raid_result') return `${message.eventData?.memberNickname || message.authorNickname} 실전 ${formatDamage(message.eventData?.damageEok)}`;
  if (message.type === 'practice_report') return `${message.eventData?.memberNickname || message.authorNickname} 모의전 ${formatDamage(message.eventData?.damageEok)}`;
  if (message.type === 'tetris_share' && message.tetrisShare) {
    if (message.tetrisShare.scope === 'global') return `테트리스 전체 ${Math.max(1, Number(message.tetrisShare.planCount || 1))}보스 계획`;
    return `${message.tetrisShare.bossName} 테트리스 ${formatDamage(message.tetrisShare.totalEok)}`;
  }
  if (message.type === 'attack_order') return `${order?.targetNickname || '멤버'} 공격 지시`;
  return String(message.body || '').trim().slice(0, 90) || '메시지';
}

function resolveMentionEntries(body, members = [], allowAll = false) {
  // @ 가 없는 글이 대부분이다. 아래 멤버 표를 먼저 만들면 그 대부분이 멤버 수만큼 헛일을 한다.
  const tokens = String(body || '').match(/@[^\s@]+/g);
  if (!tokens) return [];
  const mentionByToken = new Map(members
    .filter((member) => member.userId && member.nickname)
    .map((member) => [`@${member.nickname}`, { token: `@${member.nickname}`, userId: member.userId, nickname: member.nickname }]));
  if (allowAll) mentionByToken.set('@전체', { token: '@전체', userId: '__all__', nickname: '전체' });
  const seen = new Set();
  return tokens
    .map((token) => mentionByToken.get(token))
    .filter((entry) => entry && !seen.has(entry.token) && seen.add(entry.token));
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// 링크를 먼저 떼어낸 다음 남은 글자에서 멘션을 찾는다. 순서를 바꾸면 주소 안의 @이름이 멘션으로
// 잘려 나가 링크가 끊긴다(https://x.com/@nikke 같은 주소).
function MessageBody({ body, members, userId }) {
  const mentionByToken = new Map(resolveMentionEntries(body, members, true).map((entry) => [entry.token, entry]));
  return splitChatMessageLinks(body).map((token, index) => {
    if (token.type === 'link') {
      return <a
        className="raid-control-message-link"
        key={`link-${index}`}
        href={token.href}
        target="_blank"
        rel="noreferrer noopener"
      >{token.value}</a>;
    }
    return token.value.split(/(@[^\s@]+)/g).map((part, partIndex) => {
      const mention = mentionByToken.get(part);
      if (!mention) return part;
      const className = [
        'raid-control-message-mention',
        mention.userId === userId ? 'is-me' : '',
        mention.userId === '__all__' ? 'is-all' : '',
      ].filter(Boolean).join(' ');
      return <mark className={className} key={`${index}-${part}-${partIndex}`}>{part}</mark>;
    });
  });
}

function MemberList({ members, onlineMembers = members.filter((member) => member.online), selectedOpenid, onSelect, planActive = false, onPlanToggle = null, practiceHidden = false, onPracticeShow = null }) {
  const onlineCount = onlineMembers.length;
  const displayMembers = [...members, ...onlineMembers.filter((member) => !member.memberOpenid)]
    .sort((left, right) => Number(right.online) - Number(left.online));
  return <section className="raid-control-members" aria-label="유니온 멤버">
    <header>
      <div><strong>멤버</strong></div>
      <div className="raid-control-member-header-actions">
        <em>접속 {onlineCount} · 전체 {members.length}</em>
        {practiceHidden && onPracticeShow ? <button type="button" className="raid-control-practice-show" aria-label="멤버 모의전 펼치기" title="멤버 모의전 펼치기" onClick={onPracticeShow}><span aria-hidden="true">›</span></button> : null}
      </div>
    </header>
    {onPlanToggle ? <div className="raid-control-member-shortcuts">
      {onPlanToggle ? <button type="button" className={`raid-control-plan-entry ${planActive ? 'active' : ''}`.trim()} aria-pressed={planActive} onClick={onPlanToggle}>
        <DororaidIcon name="plan" size={18} />
        <span><strong>계획표</strong><small>단수별 5보스 계획 편집</small></span>
      </button> : null}
    </div> : null}
    <div className="raid-control-member-scroll">
      {displayMembers.map((member) => <button key={member.memberOpenid || member.userId} disabled={!member.memberOpenid} type="button" className={selectedOpenid === member.memberOpenid && !planActive ? 'active' : ''} onClick={() => onSelect(member.memberOpenid)}>
        <span className="raid-control-member-avatar">{member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : member.nickname.slice(0, 1)}</span>
        <span><span className="raid-control-member-name"><strong>{member.nickname}</strong><RaidControlMemberAttackBadge member={member} /></span><small>{member.syncLevel ? `Lv.${member.syncLevel}` : member.memberOpenid ? '레벨 미확인' : '명단 미연결'}</small></span>
        <i className={member.online ? 'online' : ''} title={member.online ? '접속 중' : '오프라인'} />
      </button>)}
    </div>
  </section>;
}

function PracticeInspector({ room, onOrder, mobile, onPracticeHide = null }) {
  const bosses = room.bosses;
  const records = room.selectedPracticeRecords.filter((record) => record.bossNo === room.selectedBossNo);
  const allRecords = useMemo(() => room.context.practiceRecords
    .filter((record) => record.memberOpenid === room.selectedMemberOpenid && Number(record.bossNo) === Number(room.selectedBossNo))
    .sort((left, right) => Number(left.slotNo) - Number(right.slotNo)), [room.context.practiceRecords, room.selectedBossNo, room.selectedMemberOpenid]);
  const boss = bosses.find((item) => item.bossNo === room.selectedBossNo);
  const hiddenCount = room.hiddenPracticeCountByBoss.get(room.selectedBossNo) || 0;
  const [editor, setEditor] = useState(null);
  const nextSlotNo = nextAvailablePracticeSlot(allRecords);
  const canManage = Boolean(room.canManageSelectedPractice && room.selectedMember?.memberOpenid && boss);

  useEffect(() => {
    setEditor(null);
  }, [room.selectedMemberOpenid, room.selectedBossNo]);

  const openCreate = () => {
    if (!canManage || !nextSlotNo) return;
    setEditor({
      member: room.selectedMember,
      boss,
      slotNo: nextSlotNo,
      record: null,
      entry: { key: `new:${room.selectedMember.memberOpenid}:${boss.bossNo}:${nextSlotNo}`, slotNo: nextSlotNo },
    });
  };
  const openEdit = (record) => {
    if (!canManage || !record?.id) return;
    setEditor({
      member: room.selectedMember,
      boss,
      slotNo: record.slotNo,
      record,
      entry: { key: `manual:${record.id}`, slotNo: record.slotNo, manual: record },
    });
  };
  const deleteRecord = async (record) => {
    if (!canManage || !record?.id || room.working) return;
    if (!window.confirm(`${Number(record.slotNo) || 1}번 모의전 슬롯을 삭제할까요?`)) return;
    await room.deletePractice({ record, targetMemberOpenid: room.selectedMember.memberOpenid });
  };

  return <>
    <section className="raid-control-inspector" aria-label="멤버 모의전 슬롯">
      <header>
        <div><strong>멤버 모의전</strong><small>{room.selectedMember?.nickname || '멤버 선택'}</small></div>
        <div className="raid-control-inspector-actions">
          {room.selectedMember?.online ? <em className="raid-control-inspector-presence">접속 중</em> : null}
          {!mobile && onPracticeHide ? <button type="button" className="raid-control-practice-collapse" aria-label="패널 접기" title="멤버 모의전 접기" onClick={onPracticeHide}>접기</button> : null}
        </div>
      </header>
      <nav className="raid-control-boss-tabs" aria-label="보스 선택">
        {bosses.map((item) => <button key={item.bossNo} type="button" className={room.selectedBossNo === item.bossNo ? 'active' : ''} onClick={() => room.selectBoss(item.bossNo)}><strong>{item.bossName}</strong><small>{item.currentPhase}단</small></button>)}
      </nav>
      {hiddenCount ? <p className="raid-control-practice-filter-note">실전에서 사용한 니케가 포함된 슬롯 {hiddenCount}개 숨김</p> : null}
      <div className="raid-control-practice-list">
        {records.length ? records.map((record) => <article key={record.id} className="expanded">
          <div className="raid-control-practice-card-head">
            <span className="raid-control-practice-head"><small>SLOT {record.slotNo}</small><strong>{record.title || `모의전 ${record.slotNo}`}</strong></span>
            <span className={`raid-control-practice-card-actions ${canManage ? '' : 'readonly'}`.trim()}>
              <strong className="raid-control-practice-head-damage">{formatDamage(record.damageEok)}</strong>
              {canManage ? <span className="raid-control-practice-card-action-icons" role="group" aria-label="모의전 슬롯 동작">
                <button type="button" className="raid-practice-slot-icon-button edit" aria-label={RAID_PRACTICE_ACTION_LABELS.edit} title={RAID_PRACTICE_ACTION_LABELS.edit} disabled={Boolean(room.working)} onClick={() => openEdit(record)}><DororaidIcon name="edit" size={mobile ? 18 : 16} /></button>
                <button type="button" className="raid-practice-slot-icon-button danger" aria-label={RAID_PRACTICE_ACTION_LABELS.remove} title={RAID_PRACTICE_ACTION_LABELS.remove} disabled={Boolean(room.working)} onClick={() => deleteRecord(record)}><DororaidIcon name="close" size={mobile ? 18 : 16} /></button>
              </span> : null}
            </span>
          </div>
          <div className="raid-control-practice-details">
            <RaidPracticeDeckDisplay deck={record.deck} mobile={mobile} />
            <PracticeProjection boss={boss} damageEok={record.damageEok} />
            {room.canDirect ? <button type="button" className="raid-control-order-open" onClick={() => onOrder(record)}>이 슬롯으로 공격 지시</button> : null}
          </div>
        </article>) : <div className="raid-control-empty"><strong>사용 가능한 슬롯 없음</strong></div>}
        {canManage && nextSlotNo ? <button type="button" className="practice-admin-add-slot" aria-label={`${nextSlotNo}번 슬롯 추가`} title={`${nextSlotNo}번 슬롯 추가`} disabled={String(room.working || '').startsWith('save:')} onClick={openCreate}><span aria-hidden="true">+</span><strong>{RAID_PRACTICE_ACTION_LABELS.add}</strong></button> : null}
      </div>
    </section>
    {editor ? <RaidPracticeEditor
      entry={editor.entry}
      slotNo={editor.slotNo}
      boss={editor.boss}
      seasonNo={room.context.season?.seasonNo}
      unionId={room.context.unionId}
      nickname={editor.member?.nickname}
      memberOpenid={editor.member?.memberOpenid}
      mobile={mobile}
      working={room.working}
      notice={room.practiceNotice}
      onSave={(payload) => editor.record
        ? room.updatePractice({ record: editor.record, targetMemberOpenid: editor.member.memberOpenid, bossNo: editor.boss.bossNo, payload })
        : room.createPractice({ targetMemberOpenid: editor.member.memberOpenid, bossNo: editor.boss.bossNo, payload })}
      onDelete={editor.record ? () => room.deletePractice({ record: editor.record, targetMemberOpenid: editor.member.memberOpenid }) : undefined}
      onClose={() => setEditor(null)}
    /> : null}
  </>;
}

function BossLiveStrip({ room }) {
  const bosses = room.bosses;
  const lastSyncAt = room.overview.selectedSeason?.lastSyncAt;
  const stale = lastSyncAt ? Date.now() - new Date(lastSyncAt).getTime() > 5 * 60_000 : true;
  return <section className="raid-control-live-bosses" aria-label="실시간 보스 체력">
    <header><strong>보스 현황</strong><span className={stale ? 'stale' : ''}>{stale ? '동기화 확인 필요' : `${timeLabel(lastSyncAt)} 갱신`}</span></header>
    <div>{bosses.map((boss) => {
      const attackers = room.attackingByBoss.get(boss.bossNo) || [];
      const attackStale = attackers.some(isStaleAttack);
      return <button key={boss.bossNo} type="button" className={attackStale ? 'attack-stale' : ''} onClick={() => room.selectBoss(boss.bossNo)}>
        <span>{boss.bossName} · {boss.currentPhase}단</span><strong>{boss.isInfinite ? '인피니티' : formatRaidHpEok(boss.remainingHpEok)}</strong>
        <i><b style={{ width: `${boss.isInfinite ? 100 : boss.remainingPercent}%` }} /></i>
        <small className={attackers.length ? `attacking${attackStale ? ' stale' : ''}` : ''}>{attackers.length ? attackers.map(attacker => `${attacker.targetNickname} 공격 중 · ${attackElapsed(attacker.startedAt)}`).join(' / ') : '공격 대기'}</small>
      </button>;
    })}</div>
  </section>;
}

function OrderActions({ order, room, userId }) {
  const confirmation = ['sent', 'acknowledged', 'held'].includes(order.status) ? room.pendingAttackConfirmations?.[order.id] : null;
  const isTarget = order.targetUserId && order.targetUserId === userId;
  const busy = room.working === `order:${order.id}`;
  if (!isTarget && !room.canDirect) return null;
  if (order.status === 'cancelled') return null;
  return <>
    <RaidAttackConfirmationNotice confirmation={confirmation} />
    {room.orderErrors?.[order.id] ? <p className="raid-control-order-error" role="alert">{room.orderErrors[order.id]}</p> : null}
    <div className="raid-control-order-actions">
    {!confirmation && isTarget && order.status === 'sent' ? <button disabled={busy} type="button" onClick={() => room.changeOrderStatus(order.id, 'acknowledged')}>확인</button> : null}
    {(isTarget || room.canDirect) && ['sent', 'acknowledged', 'held'].includes(order.status) ? <>
        <button disabled={busy} type="button" className="primary" onClick={() => room.changeOrderStatus(order.id, 'attacking', { confirmConcurrent: Boolean(confirmation) })}>{confirmation ? '확인' : '공격 시작'}</button>
        {confirmation ? <button disabled={busy} type="button" onClick={() => room.dismissAttackConfirmation(order.id)}>대기</button> : null}
      </> : null}
    {isTarget && order.status === 'attacking' ? <button disabled={busy} type="button" className="primary" onClick={() => room.changeOrderStatus(order.id, 'completed')}>공격 완료</button> : null}
    {isTarget && !['completed', 'held'].includes(order.status) ? <button disabled={busy} type="button" onClick={() => room.changeOrderStatus(order.id, 'held')}>보류</button> : null}
    {room.canDirect && order.status === 'attacking' ? <button disabled={busy} type="button" onClick={() => room.changeOrderStatus(order.id, 'held')}>상태 해제</button> : null}
    {room.canDirect ? <button disabled={busy} type="button" className="danger" onClick={() => room.changeOrderStatus(order.id, 'cancelled')}>지시 취소</button> : null}
    </div>
  </>;
}

function ReplyPreview({ replyMessage, onJump }) {
  if (!replyMessage) return null;
  return <button type="button" className="raid-control-reply-preview" onClick={() => onJump(replyMessage.id)}>
    <strong>{replyMessage.authorNickname}</strong><span>{messageSummary(replyMessage)}</span>
  </button>;
}

function ReplyAction({ message, onReply }) {
  return <span className="raid-control-message-actions"><button type="button" className="raid-control-reply-action" onClick={() => onReply(message)}>답장</button><RaidChatPinAction messageId={message.id} /></span>;
}

function chatTetrisMessageVisible(message) {
  const share = message?.type === 'tetris_share' ? message.tetrisShare : null;
  if (!share || share.scope !== 'global') return true;
  const planIndex = Number(share.planIndex);
  return !Number.isFinite(planIndex) || planIndex <= 0;
}

function tetrisChatNotice(share) {
  if (share?.scope === 'global') return `전체 ${Math.max(1, Number(share.planCount || 1))}보스 계획이 테트리스 결과에 등록됐습니다.`;
  const diff = Number(share?.diffEok || 0);
  return `${share?.bossName || '보스'} · ${share?.phaseNo || '-'}단 · ${share?.items?.length || 0}타 · 차이 ${diff >= 0 ? '+' : '-'}${formatDamage(Math.abs(diff))}`;
}

const DESKTOP_EVENT_TYPES = new Set(['tetris_share', 'raid_result', 'practice_report', 'attack_order']);

function isDesktopEventMessage(message) {
  return DESKTOP_EVENT_TYPES.has(message.type);
}

// 평범한 말풍선은 아래 값이 그대로면 다시 그릴 것이 없다. room 을 통째로 받으면 방 상태가 조금만
// 달라져도 신원이 바뀌어 memo 가 통하지 않으므로, 쓰는 값만 끊어서 받는다. 글쓴이 찾기도 부모가
// 한 번 만든 표에서 넘겨받는다 - 메시지마다 멤버 목록을 두 번 훑던 자리다.
const DesktopTextMessage = memo(function DesktopTextMessage({ message, members, authorMember, userId, replyMessage, onReply, onJump }) {
  const mine = message.authorUserId === userId;
  const authorAvatarUrl = String(authorMember?.avatarUrl || '').trim();
  return <article
    id={`raid-control-message-${message.id}`}
    data-message-id={message.id}
    className={`raid-control-chat-message ${mine ? 'mine' : ''}`}
  >
    {!mine ? <div className="raid-control-chat-avatar">{authorAvatarUrl ? <img src={authorAvatarUrl} alt="" /> : message.authorNickname.slice(0, 1)}</div> : null}
    <div>
      <header>{!mine ? <strong>{message.authorNickname}</strong> : null}<time>{timeLabel(message.createdAt)}</time><ReplyAction message={message} onReply={onReply} /></header>
      <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
      {message.type === 'sticker' ? <ChatSticker stickerId={message.stickerId} stickerIds={message.stickerIds} /> : <p><MessageBody body={message.body} members={members} userId={userId} /></p>}
      {message.nikkeRefs.length ? <div className="raid-control-message-nikkes">{message.nikkeRefs.map((nikke) => <span key={nikke.nikkeId || nikke.name}>{nikke.imageUrl ? <img src={siteImageUrl(nikke.imageUrl)} alt="" /> : null}{nikke.name}</span>)}</div> : null}
    </div>
  </article>;
});

function ChatMessage({ message, order, room, userId, mobile = false, replyMessage, onReply, onJump }) {
  const anchorProps = { id: `raid-control-message-${message.id}`, 'data-message-id': message.id };
  if (message.type === 'tetris_share' && message.tetrisShare) {
    const share = message.tetrisShare;
    const title = share.scope === 'global'
      ? `전체 ${Math.max(1, Number(share.planCount || 1))}보스 계획`
      : `${share.bossName || '보스'} · ${share.phaseNo}단`;
    return <article {...anchorProps} className={`raid-control-tetris-message ${share.sufficient ? 'enough' : 'lack'}`}>
      <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
      <header><div><span>TETRIS · {raidControlTetrisModeLabel(share.mode)}</span><strong>{title}</strong></div><em>공유</em></header>
      <p className="raid-control-tetris-chat-notice">{tetrisChatNotice(share)}</p>
      <small>{message.authorNickname} · {timeLabel(message.createdAt)}</small>
      <ReplyAction message={message} onReply={onReply} />
    </article>;
  }
  if (message.type === 'raid_result') {
    const event = message.eventData;
    return <article {...anchorProps} className={`raid-control-result-message ${event.isKill ? 'kill' : ''}`}>
      <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
      <header><div><span>실전 딜 반영</span><strong>{event.bossName || '보스'} · {event.phaseNo}단</strong></div>{event.isKill ? <em>처치</em> : null}</header>
      <div><strong>{event.memberNickname || message.authorNickname}</strong><b>{formatDamage(event.damageEok)}</b></div>
      {event.deck?.length ? <RaidPracticeDeckDisplay deck={event.deck} mobile={mobile} /> : null}
      <small>{timeLabel(event.attackedAt || message.createdAt)} 반영</small>
      <ReplyAction message={message} onReply={onReply} />
    </article>;
  }
  if (message.type === 'practice_report') {
    const event = message.eventData;
    const boss = room.bosses.find((item) => item.bossNo === event.bossNo);
    return <article {...anchorProps} className="raid-control-practice-message">
      <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
      <header><div><span>모의전 수동 입력</span><strong>{event.bossName || '보스'} · {event.phaseNo}단</strong></div><em>공유</em></header>
      <div className="raid-control-practice-message-result"><strong>{event.memberNickname || message.authorNickname}</strong><b>{formatDamage(event.damageEok)}</b></div>
      <PracticeProjection boss={boss} damageEok={event.damageEok} />
      {message.nikkeRefs.length ? <div className="raid-control-message-nikkes">{message.nikkeRefs.map((nikke) => <span key={nikke.nikkeId || nikke.name}>{nikke.imageUrl ? <img src={siteImageUrl(nikke.imageUrl)} alt="" /> : null}{nikke.name}</span>)}</div> : null}
      {message.body && message.body !== '모의전 수동 입력' ? <p>{message.body}</p> : null}
      <small>{message.authorNickname} 입력 · {timeLabel(message.createdAt)}</small>
      <ReplyAction message={message} onReply={onReply} />
    </article>;
  }
  if (message.type === 'attack_order' && order) return <article {...anchorProps} className="raid-control-order-message">
    <ReplyPreview replyMessage={replyMessage} onJump={onJump} />
    <header><div><span>공격 지시</span><strong>{order.targetNickname} → {order.bossName || '보스 정보 갱신 중'} · {order.phaseNo}단</strong></div><em className={`status-${order.status}`}>{ORDER_STATUS[order.status] || order.status}</em></header>
    <div className="raid-control-order-summary"><strong>{order.practiceTitle || '지정 덱'}</strong><div className="raid-control-order-damage"><span>예상 {formatDamage(order.expectedDamageEok)}</span></div></div>
    <RaidPracticeDeckDisplay deck={order.deck} mobile={mobile} />
    {order.note ? <p>{order.note}</p> : null}
    {isStaleAttack(order) ? <p className="raid-control-attack-stale-note">공격 시작 후 15분이 지났습니다. 상태를 확인해주세요.</p> : null}
    <OrderActions order={order} room={room} userId={userId} />
    <small>{message.authorNickname} · {timeLabel(message.createdAt)}</small>
    <ReplyAction message={message} onReply={onReply} />
  </article>;
  // 지시 메시지인데 지시 줄이 아직 안 붙은 경우만 여기로 떨어진다. 평범한 말풍선은 목록 쪽에서
  // DesktopTextMessage 로 바로 간다.
  return <DesktopTextMessage
    message={message}
    members={room.members}
    authorMember={room.members.find((member) => member.userId && member.userId === message.authorUserId)
      || room.members.find((member) => member.nickname === message.authorNickname)
      || null}
    userId={userId}
    replyMessage={replyMessage}
    onReply={onReply}
    onJump={onJump}
  />;
}

function MessageComposer({ room, userId, onCustomOrderOpen, onSelfRequestOpen, replyTo, onCancelReply }) {
  const [body, setBody] = useState('');
  const [nikkeRefs, setNikkeRefs] = useState([]);
  const [commandError, setCommandError] = useState('');
  const [sendFailed, setSendFailed] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);
  const textareaRef = useRef(null);
  const selfMember = useMemo(() => room.members.find((member) => member.userId === userId) || null, [room.members, userId]);
  const activeMentions = useMemo(() => resolveMentionEntries(body, room.members, room.canDirect), [body, room.canDirect, room.members]);
  const mentionMatch = body.match(/(?:^|\s)@([^\s@]*)$/);
  const mentionActive = Boolean(mentionMatch);
  const mentionQuery = String(mentionMatch?.[1] || '').toLocaleLowerCase('ko-KR');
  const nikkeQuery = useMemo(() => resolveRaidControlNikkeAutocompleteQuery(body), [body]);
  const mentionSuggestions = useMemo(() => {
    if (!mentionActive) return [];
    const members = room.members.filter((member) => member.userId && member.nickname.toLocaleLowerCase('ko-KR').includes(mentionQuery)).slice(0, 6);
    if (room.canDirect && '전체'.includes(mentionQuery)) return [{ userId: '__all__', nickname: '전체', online: true }, ...members].slice(0, 6);
    return members;
  }, [mentionActive, mentionQuery, room.canDirect, room.members]);
  const suggestions = useMemo(() => !mentionActive
    ? buildRaidControlNikkeAutocompleteSuggestions(room.catalog.items, nikkeQuery)
    : [], [nikkeQuery, mentionActive, room.catalog.items]);

  const selectNikke = (item) => {
    setBody((value) => value.replace(/\S+$/, '').replace(/[ \t]+$/, (spaces) => spaces ? ' ' : ''));
    setNikkeRefs((current) => current.some((entry) => entry.nikkeId === item.nikkeId) ? current : [...current, { nikkeId: item.nikkeId, name: item.name, imageUrl: item.imageUrl }].slice(0, 5));
  };
  const selectMention = (member) => {
    setBody((value) => {
      const match = value.match(/(?:^|\s)@[^\s@]*$/);
      if (!match) return `${value}${value && !/\s$/.test(value) ? ' ' : ''}@${member.nickname} `;
      const leading = match[0].match(/^\s/)?.[0] || '';
      return `${value.slice(0, match.index)}${leading}@${member.nickname} `;
    });
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };
  const removeMention = (mention) => {
    const expression = new RegExp(`(^|\\s)${escapeRegExp(mention.token)}(?=\\s|$)`, 'g');
    setBody((value) => value.replace(expression, '$1').replace(/[ \t]{2,}/g, ' ').trimStart());
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };
  const fillQuickMessage = (message) => {
    setBody(message);
    setSendFailed(false);
    setCommandError('');
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };
  const submit = async (event) => {
    event.preventDefault();
    const clean = body.trim();
    if (!clean) return;
    setSendFailed(false);
    if (clean.startsWith('/모의전')) {
      const match = clean.match(/^\/모의전\s+([0-9]+(?:\.[0-9]+)?)\s*(.*)$/);
      const boss = room.bosses.find((item) => item.bossNo === room.selectedBossNo);
      if (!match || !selfMember || !boss) {
        setCommandError('본인 멤버 정보와 보스를 확인한 뒤 /모의전 12.3 메모 형식으로 입력해주세요.');
        return;
      }
      const damageEok = Number(match[1]);
      if (!Number.isFinite(damageEok) || damageEok <= 0) {
        setCommandError('모의전 피해량은 0보다 큰 억 단위 숫자로 입력해주세요.');
        return;
      }
      const ok = await room.sendPracticeReport({
        targetMemberOpenid: selfMember.memberOpenid,
        bossNo: boss.bossNo,
        phaseNo: boss.currentPhase,
        damageEok,
        body: match[2],
        nikkeRefs,
      });
      if (ok) { setBody(''); setNikkeRefs([]); setCommandError(''); onCancelReply(); }
      else setSendFailed(true);
      return;
    }
    const mentionedUserIds = new Set(activeMentions.filter((mention) => mention.userId !== '__all__').map((mention) => mention.userId));
    if (activeMentions.some((mention) => mention.userId === '__all__')) room.members.forEach((member) => member.userId && mentionedUserIds.add(member.userId));
    const ok = await room.sendMessage({ body: clean, mentionedUserIds: [...mentionedUserIds], nikkeRefs, replyToId: replyTo?.id || null });
    if (ok) { setBody(''); setNikkeRefs([]); setCommandError(''); setSendFailed(false); onCancelReply(); }
    else setSendFailed(true);
  };
  return <form className="raid-control-composer" onSubmit={submit}>
    {replyTo ? <div className="raid-control-composer-reply"><span><small>{replyTo.authorNickname}에게 답장</small><strong>{messageSummary(replyTo)}</strong></span><button type="button" onClick={onCancelReply} aria-label="답장 취소"><DororaidIcon name="close" size={16} /></button></div> : null}
    <div className="raid-control-quick-messages" aria-label="빠른 메시지">{QUICK_MESSAGES.map((message) => <button type="button" key={message} onClick={() => fillQuickMessage(message)}>{message}</button>)}</div>
    {mentionSuggestions.length ? <div className="raid-control-nikke-suggestions raid-control-mention-suggestions" aria-label="멤버 멘션 선택">{mentionSuggestions.map((member) => <button key={member.userId} type="button" onClick={() => selectMention(member)}>{member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : null}<span><strong>@{member.nickname}</strong><small>{member.userId === '__all__' ? '운영진 전체 멘션' : member.online ? '접속 중' : '멤버 멘션'}</small></span></button>)}</div> : null}
    {!mentionSuggestions.length && suggestions.length ? <div className="raid-control-nikke-suggestions">{suggestions.map((item) => <button key={item.nikkeId} type="button" onClick={() => selectNikke(item)}>{item.imageUrl ? <img src={siteImageUrl(item.imageUrl)} alt="" /> : null}<span><strong>{item.name}</strong><small>{item.aliases.join(' · ') || item.element}</small></span></button>)}</div> : null}
    {activeMentions.length ? <div className="raid-control-composer-mentions" aria-label="적용된 멘션"><small>멘션 적용</small>{activeMentions.map((mention) => <button type="button" key={mention.token} onClick={() => removeMention(mention)} title={`${mention.token} 멘션 제거`}><strong>{mention.token}</strong><span>{mention.userId === '__all__' ? '전체 알림' : '알림 대상'}</span><DororaidIcon name="close" size={13} /></button>)}</div> : null}
    {nikkeRefs.length ? <div className="raid-control-composer-nikkes" aria-label="선택한 니케">{nikkeRefs.map((item) => <button type="button" key={item.nikkeId} aria-label={`${item.name} 선택 해제`} title={`${item.name} 선택 해제`} onClick={() => setNikkeRefs((current) => current.filter((entry) => entry.nikkeId !== item.nikkeId))}><span>{item.name}</span><DororaidIcon name="close" size={12} /></button>)}</div> : null}
    {commandError ? <p className="raid-control-command-error">{commandError}</p> : null}
    {sendFailed ? <p className="raid-control-command-error">전송에 실패했습니다. 입력 내용은 유지했습니다.</p> : null}
    {plusOpen ? <div className="raid-control-plus-menu" role="menu">
      <button className="raid-control-plus-action" role="menuitem" type="button" onClick={() => { setPlusOpen(false); onSelfRequestOpen(); }}><strong>내 공격덱 등록</strong><small>내 공격 덱을 등록하고 승인 요청</small></button>
      {room.canDirect ? <button className="raid-control-plus-action" role="menuitem" type="button" onClick={() => { setPlusOpen(false); onCustomOrderOpen(); }}><strong>공격 지시 작성</strong><small>저장 슬롯 또는 새 덱 직접 작성</small></button> : null}
    </div> : null}
    <div className="raid-control-composer-row"><button type="button" className="raid-control-plus" title="관제 기능 열기" aria-label="관제 기능 열기" aria-expanded={plusOpen} onClick={() => setPlusOpen((open) => !open)}>+</button><textarea ref={textareaRef} value={body} maxLength={500} rows={1} placeholder="메시지, @닉네임, <니케 검색> 또는 /모의전 피해량" onChange={(event) => { setBody(event.target.value); setCommandError(''); setSendFailed(false); }} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} /><ChatStickerPicker room={room} onOpen={() => setPlusOpen(false)} /><button type="submit" disabled={!body.trim() || ['message', 'practice'].includes(room.working)}><DororaidIcon name="chevronRight" size={19} /><span>전송</span></button></div>
  </form>;
}

function ChatPanel({ room, userId, mobile = false, onCustomOrderOpen, onSelfRequestOpen }) {
  const scrollRef = useRef(null);
  const { loadOlder, onHistoryScroll } = useRaidChatHistoryScroll(scrollRef, room);
  const initialSeasonRef = useRef('');
  const lastMessageIdRef = useRef('');
  const wasHistoryRef = useRef(false);
  const lastMarkedAtRef = useRef('');
  const [replyTo, setReplyTo] = useState(null);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const chatMessages = useMemo(() => (mobile ? room.messages : selectDesktopChatMessages(room.messages)).filter(chatTetrisMessageVisible), [room.messages, mobile]);
  const messageById = useMemo(() => new Map(room.messages.map((message) => [message.id, message])), [room.messages]);
  const unread = useMemo(() => {
    if (!room.unreadAnchorId) return { anchorId: '', count: 0 };
    const startIndex = room.messages.findIndex((message) => message.id === room.unreadAnchorId);
    if (startIndex < 0) return { anchorId: '', count: 0 };
    const visibleUnread = room.messages.slice(startIndex)
      .filter((message) => chatMessages.includes(message) && message.authorUserId !== userId);
    return { anchorId: visibleUnread[0]?.id || '', count: visibleUnread.length };
  }, [room.messages, room.unreadAnchorId, userId, chatMessages]);
  const memberIndex = useMemo(() => {
    const byUserId = new Map();
    const byNickname = new Map();
    room.members.forEach((member) => {
      if (member.userId && !byUserId.has(member.userId)) byUserId.set(member.userId, member);
      if (member.nickname && !byNickname.has(member.nickname)) byNickname.set(member.nickname, member);
    });
    return { byUserId, byNickname };
  }, [room.members]);
  // 종류 가리기와 글쓴이 찾기는 목록이 바뀔 때만 하면 된다. 렌더마다 하면 글 하나가 도착할 때
  // 화면에 올라온 메시지 전부를 다시 훑는다.
  const chatEntries = useMemo(() => chatMessages.map((message) => ({
    message,
    event: isDesktopEventMessage(message),
    authorMember: memberIndex.byUserId.get(message.authorUserId)
      || memberIndex.byNickname.get(message.authorNickname)
      || null,
  })), [chatMessages, memberIndex]);
  const nearBottom = (node) => node.scrollHeight - node.scrollTop - node.clientHeight < 64;
  // ref 와 DOM 만 쓴다. 매 렌더마다 새 함수를 넘기면 말풍선 memo 가 전부 깨진다.
  const jumpToMessage = useCallback((messageId) => {
    const node = scrollRef.current?.querySelector(`[data-message-id="${messageId}"]`);
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    node?.classList.add('reply-target-flash');
    window.setTimeout(() => node?.classList.remove('reply-target-flash'), 1200);
  }, []);
  const markLatestRead = () => {
    const latestAt = room.messages[room.messages.length - 1]?.createdAt || '';
    if (!latestAt || lastMarkedAtRef.current === latestAt) return;
    lastMarkedAtRef.current = latestAt;
    room.markReadThrough(latestAt);
  };
  const scrollToLatest = (behavior = 'smooth') => {
    if (room.historyMode) { room.showLatestMessages(); return; }
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior });
    setNewMessageCount(0);
    window.requestAnimationFrame(markLatestRead);
  };

  useEffect(() => {
    const seasonKey = String(room.context.season?.id || '');
    if (!seasonKey || !chatMessages.length || initialSeasonRef.current === seasonKey) return;
    initialSeasonRef.current = seasonKey;
    lastMessageIdRef.current = chatMessages[chatMessages.length - 1]?.id || '';
    window.requestAnimationFrame(() => {
      if (!unread.anchorId) { scrollToLatest('auto'); return; }
      jumpToMessage(unread.anchorId);
      // 읽음은 스크롤 이벤트에서만 찍었다. 채팅이 짧아 스크롤할 게 없거나 안 읽은 줄이 이미 맨
      // 아래 근처면 스크롤 이벤트가 안 와서, 다 보고 나가도 관제 탭 빨간 점이 남았다(2026-10-20).
      // 그 경우는 지금 바로 찍는다. 실제로 내려가야 하면 도착할 때 handleScroll 이 찍는다.
      const node = scrollRef.current;
      if (node && nearBottom(node)) markLatestRead();
    });
  }, [chatMessages, room.context.season?.id, unread.anchorId]);

  useEffect(() => {
    if (wasHistoryRef.current && !room.historyMode) {
      wasHistoryRef.current = false;
      lastMessageIdRef.current = chatMessages[chatMessages.length - 1]?.id || '';
      setNewMessageCount(0);
      markLatestRead();
      return;
    }
    wasHistoryRef.current = room.historyMode;
    const latest = chatMessages[chatMessages.length - 1];
    if (!latest) return;
    const previousId = lastMessageIdRef.current;
    if (!previousId) {
      lastMessageIdRef.current = latest.id;
      return;
    }
    if (room.historyMode || previousId === latest.id) return;
    const previousIndex = chatMessages.findIndex((message) => message.id === previousId);
    const added = (previousIndex >= 0 ? chatMessages.slice(previousIndex + 1) : [latest]).filter((message) => message.authorUserId !== userId).length;
    lastMessageIdRef.current = latest.id;
    const node = scrollRef.current;
    if (node && nearBottom(node)) scrollToLatest('smooth');
    else if (added) setNewMessageCount((count) => count + added);
  }, [chatMessages, userId, room.historyMode]);

  const handleScroll = () => {
    onHistoryScroll();
    if (room.historyMode) return;
    const node = scrollRef.current;
    if (!node || !nearBottom(node)) return;
    setNewMessageCount(0);
    markLatestRead();
  };
  const pendingCount = unread.count + newMessageCount;

  return <section className="raid-control-chat" aria-label="관제 채팅">
    {mobile ? <BossLiveStrip room={room} /> : <div className="raid-control-operations-header"><BossLiveStrip room={room} /></div>}
    <div className="raid-control-chat-head"><div><strong>관제 채팅</strong></div><em>실시간</em></div>
    <div className="raid-control-chat-header-slot"><RaidChatHeader key={room.context.season?.id} room={room} /></div>
    <RaidChatPinProvider room={room}><div className="raid-control-message-list" ref={scrollRef} data-history={room.historyMode ? 'true' : undefined} onScroll={handleScroll}><RaidChatHistoryControls room={room} onLoadOlder={loadOlder} />{chatEntries.length ? chatEntries.map(({ message, event, authorMember }) => <div className="raid-control-message-entry" key={message.id}>
      {unread.anchorId === message.id ? <div className="raid-control-unread-divider"><span>여기부터 안 읽은 메시지</span><em>{unread.count}개</em></div> : null}
      {event ? <ChatMessage message={message} order={room.ordersById.get(message.orderId)} room={room} userId={userId} mobile={mobile} replyMessage={messageById.get(message.replyToId) || null} onReply={setReplyTo} onJump={jumpToMessage} />
        : <DesktopTextMessage message={message} members={room.members} authorMember={authorMember} userId={userId} replyMessage={messageById.get(message.replyToId) || null} onReply={setReplyTo} onJump={jumpToMessage} />}
    </div>) : <div className="raid-control-empty"><strong>관제 메시지 없음</strong></div>}</div></RaidChatPinProvider>
    {pendingCount ? <button type="button" className="raid-control-new-message-jump" onClick={() => unread.anchorId ? jumpToMessage(unread.anchorId) : scrollToLatest()}><span>새 메시지 {pendingCount}개</span><DororaidIcon name="chevronDown" size={16} /></button> : null}
    <MessageComposer room={room} userId={userId} onCustomOrderOpen={onCustomOrderOpen} onSelfRequestOpen={onSelfRequestOpen} replyTo={replyTo} onCancelReply={() => setReplyTo(null)} />
  </section>;
}

function StrategyPlanView() {
  return <section className="raid-control-chat raid-control-strategy-result-view" aria-label="계획표">
    <RaidControlStrategyPanel />
  </section>;
}

export function RaidControlRoom({ user, mobile = false, onSelfRequestOpen = () => {}, onOrderOpen = () => {}, room, mobilePanel: controlledMobilePanel = null, onMobilePanelChange = null, desktopView: controlledDesktopView = null, onDesktopViewChange = null, desktopOrders = null }) {
  const [internalMobilePanel, setInternalMobilePanel] = useState('chat');
  const mobilePanel = controlledMobilePanel ?? internalMobilePanel;
  const setMobilePanel = (nextPanel) => {
    if (controlledMobilePanel !== null && controlledMobilePanel !== undefined) {
      onMobilePanelChange?.(nextPanel);
      return;
    }
    setInternalMobilePanel(nextPanel);
  };
  const [internalDesktopView, setInternalDesktopView] = useState('chat');
  const [practiceHidden, setPracticeHidden] = useState(true);
  const desktopView = controlledDesktopView ?? internalDesktopView;
  const setDesktopView = (nextView) => {
    if (controlledDesktopView !== null && controlledDesktopView !== undefined) {
      onDesktopViewChange?.(typeof nextView === 'function' ? nextView(desktopView) : nextView);
      return;
    }
    setInternalDesktopView(nextView);
  };
  const selectMember = (openid) => {
    room.selectMember(openid);
    if (mobile) setMobilePanel('practice');
    else {
      setPracticeHidden(false);
      setDesktopView('chat');
    }
  };
  const toggleDesktopPlan = () => {
    setPracticeHidden(true);
    setDesktopView((current) => current === 'plan' ? 'chat' : 'plan');
  };

  if (room.loading) return <section className="raid-control-state"><LoadingState label="관제 데이터 동기화 중" variant="chat" /></section>;
  if (room.errorMessage && !room.context.season) return <section className="raid-control-state error"><strong>관제를 열지 못했습니다.</strong><p>{room.errorMessage}</p><button type="button" onClick={() => room.refresh()}>다시 확인</button></section>;

  return <section className={`raid-control-room ${mobile ? 'mobile' : 'desktop'}${!mobile && practiceHidden ? ' practice-hidden' : ''}`}>
    {mobile ? <nav className="raid-control-mobile-tabs"><button className={mobilePanel === 'members' ? 'active' : ''} type="button" onClick={() => setMobilePanel('members')}>멤버</button><button className={mobilePanel === 'practice' ? 'active' : ''} type="button" onClick={() => setMobilePanel('practice')}>모의전</button><button className={mobilePanel === 'chat' ? 'active' : ''} type="button" onClick={() => setMobilePanel('chat')}>채팅</button></nav> : null}
    {!mobile ? <DesktopRaidControlLayout
      members={<MemberList members={room.members} onlineMembers={room.onlineMembers} selectedOpenid={practiceHidden ? '' : room.selectedMemberOpenid} onSelect={selectMember} planActive={desktopView === 'plan'} onPlanToggle={toggleDesktopPlan} practiceHidden={practiceHidden} onPracticeShow={() => setPracticeHidden(false)} />}
      timeline={desktopView === 'plan'
        ? <StrategyPlanView />
        : <ChatPanel room={room} userId={String(user?.id || '')} onCustomOrderOpen={() => onOrderOpen(null, 'custom')} onSelfRequestOpen={onSelfRequestOpen} />}
      orders={desktopOrders}
      recentResults={<DesktopRecentResults messages={room.messages} />}
      practice={!practiceHidden ? <PracticeInspector room={room} mobile={false} onPracticeHide={() => setPracticeHidden(true)} onOrder={(record) => onOrderOpen(record, 'saved')} /> : null}
    /> : <div className="raid-control-grid">
      {(!mobile || mobilePanel === 'members') ? <MemberList members={room.members} onlineMembers={room.onlineMembers} selectedOpenid={room.selectedMemberOpenid} onSelect={selectMember} practiceHidden={!mobile && practiceHidden} onPracticeShow={!mobile ? () => setPracticeHidden(false) : null} /> : null}
      {(!mobile || mobilePanel === 'practice') && (mobile || !practiceHidden) ? <PracticeInspector room={room} mobile={mobile} onPracticeHide={!mobile ? () => setPracticeHidden(true) : null} onOrder={(record) => onOrderOpen(record, 'saved')} /> : null}
      {(!mobile || mobilePanel === 'chat') ? <ChatPanel room={room} userId={String(user?.id || '')} mobile={mobile} onCustomOrderOpen={() => onOrderOpen(null, 'custom')} onSelfRequestOpen={onSelfRequestOpen} /> : null}
    </div>}
    {room.errorMessage ? <p className="raid-control-inline-error">{room.errorMessage}</p> : null}
  </section>;
}
